import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import * as studentWorksheetTypes from "Store/students/studentsWorksheets/constants";
import * as globalActions from "Store/global/actions";
import {
  setActionSpinner,
  setFetchSpinner,
  setGlobalSpinner,
  setGlobalTemplateLoader
} from "Store/global/actions";
import {
  addBulkAnswersToWorksheetApi,
  assignWorksheetToStudentApi,
  excuseStudentWorksheetLatenessApi,
  fetchStudentWorksheetApi,
  fetchStudentWorksheetByCategoryApi,
  fetchStudentWorksheetsApi,
  markAllWorksheetFlagsReviewedApi,
  rescheduleStudentWorksheetsApi,
  resetStudentWorksheetsApi,
  unassignStudentWorksheetsApi,
  updateStudentWorksheetProblemFlagApi,
  updateStudentWorksheetStatusApi
} from "Store/students/studentsWorksheets/api";
import { fetchAllCalendarWorksheetsApi, fetchStudentApi } from "Store/students/api";
import { Toast } from "utils/toast";
import SUCCESSES from "constants/localization/successes";
import ERRORS from "constants/localization/errors";
import * as studentWorksheetActions from "Store/students/studentsWorksheets/actions";
import { makeSelectActiveStudent, makeSelectActiveWorksheet } from "Store/students/selectors";
import { convertMetaToPagination, renderApiParameters } from "Store/global/saga";
import * as globalSelectors from "Store/global/selectors";
import { fetchWorksheetTemplateApi } from "Store/global/api";
import { setActiveStudent } from "Store/students/actions";
import { makeSelectActiveClassroom } from "Store/classrooms/selectors";

function* handleFetchStudentWorksheetsSaga() {
  yield put(setFetchSpinner(true));
  try {
    const activeStudent = yield select(makeSelectActiveStudent);
    const parameters = yield renderApiParameters(globalSelectors, "studentWorksheets");
    const { data: worksheets, meta } = yield fetchStudentWorksheetsApi(
      activeStudent.id,
      parameters
    );
    yield put(globalActions.setPagination(convertMetaToPagination(meta), "studentWorksheets"));
    yield put(studentWorksheetActions.setStudentWorksheets(worksheets));
  } catch (e) {
    Toast.error(ERRORS.handleFetchStudentWorksheetsSaga, e.message);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleUpdateStudentWorksheetProblemFlagStatus(action) {
  yield put(setActionSpinner(true));
  try {
    const { error } = yield updateStudentWorksheetProblemFlagApi(action.payload);
    if (error) {
      Toast.error(ERRORS.handleUpdateStudentWorksheetProblemFlagStatus, error);
    } else {
      yield put(studentWorksheetActions.setStudentWorksheetProblemFlag(action.payload));
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateStudentWorksheetProblemFlagStatus, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleAssignStudentWorksheetsSaga(action) {
  yield put(globalActions.setActionSpinner(true));
  try {
    let errors = [];
    if (action.source === "calendar") {
      const subResponse = yield call(assignWorksheetToStudentApi, action.payload);
      if (subResponse && subResponse.message) {
        errors.push(subResponse.message);
      }
    } else {
      const numberOfPages = Math.ceil(action.payload.worksheet_ids.length / 30);

      for (let i = 1; i <= numberOfPages; i++) {
        const min = (i - 1) * 30;
        const max = i * 30;

        const payload = {
          student_id: action.payload.student_id,
          assignment_date: action.payload.assignment_date,
          due_date: action.payload.due_date,
          worksheet_ids: action.payload.worksheet_ids.slice(
            min,
            i === numberOfPages ? action.payload.worksheet_ids.length : max
          )
        };

        if (payload.worksheet_ids.length) {
          const subResponse = yield assignWorksheetToStudentApi(payload);
          if (subResponse && subResponse.message) {
            errors.push(subResponse.message);
          }
        }
      }
    }
    if (Boolean(errors?.length)) {
      yield put(globalActions.setErrorSignal(true));
      Toast.error(ERRORS.handleAssignWorksheet, errors.join(", "));
    } else {
      Toast.success(SUCCESSES.handleAssignWorksheet);
      if (action.source === "calendar") {
        yield call(handleFetchStudentCalendarWorksheetsSaga);
      } else if (action.source === "list") {
        yield call(handleFetchStudentWorksheetsSaga);
      }
    }
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleAssignWorksheet, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleRescheduleWorksheetsSaga(action) {
  const { payload, source } = action;

  yield put(setActionSpinner(true));
  try {
    const { error } = yield rescheduleStudentWorksheetsApi({ student_worksheets: payload });
    if (error) {
      yield put(globalActions.setErrorSignal(true));
      Toast.error(ERRORS.handleRescheduleStudentWorksheets, error);
    } else {
      Toast.success(SUCCESSES.handleRescheduleStudentWorksheets);

      if (source === "calendar") {
        yield put(globalActions.setFetchSpinner(true));
        yield call(handleFetchStudentCalendarWorksheetsSaga);
      } else {
        yield call(handleFetchStudentWorksheetsSaga);
      }
    }
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleRescheduleStudentWorksheets, error);
  } finally {
    yield put(setActionSpinner(false));
    yield put(globalActions.setFetchSpinner(false));
  }
}

function* handleSubmitBulkWorksheetAnswersSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const { answers } = action.payload;
    const activeStudent = yield select(makeSelectActiveStudent);
    const activeWorksheet = yield select(makeSelectActiveWorksheet);
    const { error } = yield addBulkAnswersToWorksheetApi({
      student_worksheet_id: activeWorksheet.id,
      answers
    });
    if (error) {
      Toast.error(ERRORS.handleSubmitBulkWorksheetAnswers, error);
    } else {
      const { data: updatedWorksheet } = yield fetchStudentWorksheetApi(
        activeStudent.id,
        activeWorksheet.id
      );

      activeWorksheet.scoring = updatedWorksheet.scoring;
      yield put(studentWorksheetActions.setActiveWorksheet(activeWorksheet));
      Toast.success(SUCCESSES.handleUpdateWorksheetAnswers);
    }
  } catch (error) {
    Toast.error(ERRORS.handleSubmitBulkWorksheetAnswers, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleUnassignStudentWorksheetSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const { error } = yield unassignStudentWorksheetsApi({
      student_worksheet_ids: action.payload
    });
    if (error) {
      return Toast.error(ERRORS.handleUnAssignWorksheet, error);
    }
    yield call(handleFetchStudentWorksheetsSaga);
  } catch (error) {
    Toast.error(ERRORS.handleUnAssignWorksheet, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleExcuseStudentWorksheetLatenessSaga(
  action: studentWorksheetTypes.IHandleExcuseStudentWorksheetLateness
) {
  yield put(setActionSpinner(true));
  try {
    let errors = [];
    for (let item of action.payload) {
      const response = yield excuseStudentWorksheetLatenessApi(item);
      if (response?.message) {
        errors.push(response?.message);
      }
    }
    if (errors?.length) {
      Toast.error(ERRORS.handleExcuseStudentWorksheetLateness, errors.join(", "));
    } else {
      Toast.success(SUCCESSES.handleExcuseStudentWorksheetLateness);
    }
  } catch (error) {
    Toast.error(ERRORS.handleExcuseStudentWorksheetLateness, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleMarkAllWorksheetFlagsReviewed(
  action: studentWorksheetTypes.IHandleMarkAllWorksheetFlagsReviewed
) {
  yield setActionSpinner(true);
  try {
    const { error } = yield markAllWorksheetFlagsReviewedApi({
      student_worksheet_ids: action.payload
    });

    if (error) {
      Toast.error(ERRORS.handleMarkAllWorksheetFlagsReviewed, error);
    } else {
      if (action.single) {
        yield call(handleOpenWorksheetDetailsSaga, {
          item: {
            id: action.payload[0]
          }
        });
        const activeStudent = yield select(makeSelectActiveStudent);
        const { data: studentResponse } = yield fetchStudentApi(activeStudent.id);

        yield put(setActiveStudent({ ...studentResponse }));
      } else {
        yield call(handleFetchStudentWorksheetsSaga);
      }
    }

    Toast.success(SUCCESSES.handleMarkAllWorksheetFlagsReviewed);
  } catch (error) {
    Toast.error(ERRORS.handleMarkAllWorksheetFlagsReviewed, error);
  } finally {
    yield setActionSpinner(false);
  }
}

function* handleResetStudentWorksheetsSaga(
  action: studentWorksheetTypes.IHandleResetStudentWorksheets
) {
  yield put(setGlobalSpinner(true));
  try {
    const total = action.payload.length;
    const numberOfPages = Math.ceil(total / 30);
    for (let i = 1; i <= numberOfPages; i++) {
      const min = (i - 1) * 30;
      const max = i * 30;

      yield call(resetStudentWorksheetsApi, {
        student_worksheet_ids: action.payload.slice(min, i === numberOfPages ? total : max)
      });
    }

    yield call(handleFetchStudentWorksheetsSaga);
    Toast.success(SUCCESSES.handleResetWorksheets);
  } catch (error) {
    Toast.error(ERRORS.handleResetWorksheets, error);
  } finally {
    yield put(setGlobalSpinner(false));
  }
}

export function* handleFetchStudentCalendarWorksheetsSaga() {
  try {
    const activeStudent = yield select(makeSelectActiveStudent);

    if (activeStudent) {
      const { data, error } = yield fetchAllCalendarWorksheetsApi(activeStudent?.id);
      if (error) {
        return Toast.error(ERRORS.handleGetStudentCalendarWorksheets, error);
      }
      yield put(studentWorksheetActions.setStudentWorksheets(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleGetStudentCalendarWorksheets, error);
  }
}

export function* handleOpenWorksheetDetailsSaga(action) {
  const { item } = action;
  try {
    const { id: studentId } = yield select(makeSelectActiveStudent);
    let fetchedItem;
    const { data: studentWorksheet, error } = yield fetchStudentWorksheetApi(studentId, item.id);
    if (error) {
      Toast.error(ERRORS.handleOpenSelectedItemDetailsSaga, error);
      return null;
    }
    fetchedItem = studentWorksheet;
    if (!fetchedItem.problems.length) {
      const { data: worksheetTemplate } = yield fetchWorksheetTemplateApi(fetchedItem.worksheet.id);
      const problems = worksheetTemplate.problems.map((wwp) =>
        fetchedItem.problems.find((p) => p.problem.id === wwp.id)
      );
      fetchedItem = { ...fetchedItem, problems };
    }

    yield put(studentWorksheetActions.setActiveWorksheet(fetchedItem));
    return fetchedItem;
  } catch (error) {
    Toast.error(ERRORS.handleOpenWorksheetDetailsSaga);
  }
}

function* handleStartStudentWorksheetSaga() {
  yield put(setActionSpinner(true));
  try {
    const activeWorksheet = yield select(makeSelectActiveWorksheet);

    const { error } = yield updateStudentWorksheetStatusApi({
      student_worksheet_id: activeWorksheet?.id,
      status: "STARTED"
    });

    if (error) {
      Toast.error(ERRORS.handleStartStudentWorksheetSaga, error);
    } else {
      activeWorksheet.status = "STARTED";
      yield put(studentWorksheetActions.setActiveWorksheet(activeWorksheet));
    }
  } catch (error) {
    Toast.error(ERRORS.handleStartStudentWorksheetSaga, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleFetchStudentWorksheetsByCategoriesSaga(action) {
  yield put(setGlobalTemplateLoader(true));

  try {
    const activeStudent = yield select(makeSelectActiveStudent);
    const activeClassroom = yield select(makeSelectActiveClassroom);
    const parameters = yield renderApiParameters(globalSelectors, "worksheetsByCategories");
    const { data, error, meta } = yield fetchStudentWorksheetByCategoryApi(
      activeStudent.id,
      `${parameters}&category_id=${action.payload.join(",")}`
    );
    if (error) {
      Toast.error(ERRORS.handleFetchStudentWorksheetsByCategories, error);
    } else {
      yield put(
        globalActions.setPagination(convertMetaToPagination(meta), "worksheetsByCategories")
      );
      yield put(globalActions.setWorksheetsByCategory(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchStudentWorksheetsByCategories, error);
  } finally {
    yield put(setGlobalTemplateLoader(false));
  }
}

export default function* studentsWorksheetsSaga() {
  /* ===================== WORKSHEETS */
  yield takeLatest(
    studentWorksheetTypes.HANDLE_RESCHEDULE_WORKSHEETS,
    handleRescheduleWorksheetsSaga
  );
  yield takeLatest(
    studentWorksheetTypes.HANDLE_ASSIGN_STUDENT_WORKSHEETS,
    handleAssignStudentWorksheetsSaga
  );
  yield takeLatest(
    studentWorksheetTypes.HANDLE_UNASSIGN_STUDENT_WORKSHEETS,
    handleUnassignStudentWorksheetSaga
  );
  yield takeLatest(
    studentWorksheetTypes.HANDLE_FETCH_STUDENT_WORKSHEETS,
    handleFetchStudentWorksheetsSaga
  );
  yield takeLatest(
    studentWorksheetTypes.HANDLE_EXCUSE_STUDENT_WORKSHEET_LATENESS,
    handleExcuseStudentWorksheetLatenessSaga
  );
  yield takeLatest(
    studentWorksheetTypes.HANDLE_FETCH_STUDENT_CALENDAR_WORKSHEETS,
    handleFetchStudentCalendarWorksheetsSaga
  );
  yield takeEvery(
    studentWorksheetTypes.HANDLE_MARK_ALL_WORKSHEET_FLAGS_REVIEWED,
    handleMarkAllWorksheetFlagsReviewed
  );
  yield takeLatest(
    studentWorksheetTypes.HANDLE_RESET_STUDENT_WORKSHEETS,
    handleResetStudentWorksheetsSaga
  );

  // ------ NOT REFACTORED WORKSHEETS GENERATORS

  yield takeLatest(
    studentWorksheetTypes.HANDLE_UPDATE_STUDENT_WORKSHEET_PROBLEM_FLAG_STATUS,
    handleUpdateStudentWorksheetProblemFlagStatus
  );
  yield takeLatest(
    studentWorksheetTypes.HANDLE_SUBMIT_BULK_WORKSHEET_ANSWERS,
    handleSubmitBulkWorksheetAnswersSaga
  );

  yield takeLatest(
    studentWorksheetTypes.HANDLE_START_STUDENT_WORKSHEET,
    handleStartStudentWorksheetSaga
  );
  yield takeLatest(
    studentWorksheetTypes.HANDLE_FETCH_STUDENT_WORKSHEETS_BY_CATEGORIES,
    handleFetchStudentWorksheetsByCategoriesSaga
  );
}
